import { useState } from 'react';
import { CheckCircle2, AlertCircle, Clock, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import type { ActionExecution } from '../types';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const STORAGE_KEY = 'halo:admin:ops-history';
const MAX_ENTRIES = 50;

export function loadHistory(): ActionExecution[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ActionExecution[]) : [];
  } catch {
    return [];
  }
}

export function saveHistory(history: ActionExecution[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(0, MAX_ENTRIES)));
}

export function ActionHistory({ history, onClear }: { history: ActionExecution[]; onClear: () => void }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (history.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 text-center">
        <Clock className="w-6 h-6 text-gray-300 mx-auto mb-2" />
        <p className="text-xs text-gray-400 dark:text-gray-500">No operations executed yet. Results will appear here.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">History</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{history.length} entries</span>
        </div>
        <button
          onClick={onClear}
          className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-red-500 flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" /> Clear
        </button>
      </div>
      <div className="divide-y divide-gray-100 max-h-[400px] overflow-auto">
        {history.map((entry) => {
          const isExpanded = expandedId === entry.id;
          const StatusIcon = entry.status === 'success' ? CheckCircle2 : AlertCircle;
          const statusColor = entry.status === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-500';

          return (
            <div key={entry.id}>
              <button
                onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors text-left"
              >
                <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${statusColor}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{entry.actionName}</div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-2">
                    <span title={new Date(entry.timestamp).toLocaleString()}>{timeAgo(entry.timestamp)}</span>
                    {entry.durationMs != null && <span>{entry.durationMs}ms</span>}
                    {Object.keys(entry.inputs).length > 0 && (
                      <span className="truncate max-w-[200px]">
                        {Object.entries(entry.inputs).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-300" /> : <ChevronRight className="w-3 h-3 text-gray-300" />}
              </button>
              {isExpanded && (
                <div className="px-4 pb-3 pt-0">
                  {entry.error && (
                    <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg p-2 mb-1">
                      <p className="text-[10px] text-red-600 dark:text-red-400">{entry.error}</p>
                    </div>
                  )}
                  {entry.result != null && (
                    <pre className="text-[10px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-950 rounded-lg p-2 overflow-auto max-h-32">
                      {JSON.stringify(entry.result, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
